import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { formatCOP, formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import {
  Landmark, TrendingUp, Ticket, Award, ShoppingCart, Package, Wallet,
  Truck, CheckCircle2, Clock, XCircle, RefreshCcw, ArrowRightLeft,
  ShieldAlert, Building2, ChevronRight, Download, FileSpreadsheet, FileText,
  ShieldCheck, ShieldOff, Calendar,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { AmountMismatchAlert } from '@/components/AmountMismatchAlert';

// ─── Copy tables ────────────────────────────────────────────────────────
const CONCEPT_META = {
  activation: { label: 'Activaciones', icon: Ticket, tone: 'text-blue-700 bg-blue-50 border-blue-200' },
  plan:       { label: 'Planes',       icon: Award,  tone: 'text-purple-700 bg-purple-50 border-purple-200' },
  sponsorship:{ label: 'Patrocinios',  icon: TrendingUp, tone: 'text-amber-700 bg-amber-50 border-amber-200' },
  commission: { label: 'Comisiones',   icon: ShoppingCart, tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
};

const REVENUE_STATUS = {
  expected: { label: 'Esperado', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  settled:  { label: 'Confirmado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  reversed: { label: 'Revertido', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
};

const PAYOUT_STATUS = {
  pending_delivery: { label: 'Pendiente por entrega', cls: 'bg-slate-100 text-slate-800 border-slate-200', icon: Clock },
  ready:            { label: 'Listo para desembolso', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  scheduled:        { label: 'Programado', cls: 'bg-blue-100 text-blue-800 border-blue-200', icon: ArrowRightLeft },
  sent:             { label: 'Enviado', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Truck },
  completed:        { label: 'Completado', cls: 'bg-teal-100 text-teal-800 border-teal-200', icon: CheckCircle2 },
  held:             { label: 'Retenido', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: ShieldAlert },
  refunded:         { label: 'Reembolsado', cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: RefreshCcw },
  settled_no_transfer: { label: 'Liquidado sin transferencia', cls: 'bg-slate-200 text-slate-800 border-slate-300', icon: CheckCircle2 },
};

const REFUND_STATUS = {
  requested: { label: 'Solicitado', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved:  { label: 'Aprobado', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  processed: { label: 'Procesado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected:  { label: 'Rechazado', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
};

const PAYOUT_MODE_LABEL = {
  manual:         'Manual (retiros aprobados por admin)',
  split_gateway:  'Split gateway (Wompi Marketplace / PayU / dLocal)',
  payouts_api:    'Payouts API (transferencias automáticas)',
};

// ─── Page ───────────────────────────────────────────────────────────────
export default function AdminFinance() {
  const [tab, setTab] = useState(() => {
    const qs = new URLSearchParams(window.location.search).get('tab');
    return qs === 'seller' || qs === 'withdrawals' || qs === 'refunds' ? qs : 'automarket';
  });
  const [overview, setOverview] = useState(null);

  useSEO({
    title: 'Finanzas | Administración',
    description: 'Ingresos de AutoMarket y dinero de los vendedores en dos vistas separadas.',
    canonical: '/admin/finanzas',
  });

  const refreshOverview = () =>
    api.get('/admin/finance/overview').then(({ data }) => setOverview(data)).catch(() => {});

  useEffect(() => { refreshOverview(); }, []);

  const setTabAndUrl = (t) => {
    setTab(t);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', t);
    window.history.replaceState({}, '', url.toString());
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-2">
              <Landmark className="h-7 w-7 text-slate-700" /> Finanzas
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Dinero de AutoMarket y dinero de los vendedores en cuentas separadas. Cada movimiento queda registrado con historial y trazabilidad.
            </p>
          </div>
          {overview && (
            <div className="text-right text-xs" data-testid="admin-finance-payout-mode">
              <div className="uppercase font-bold tracking-[0.2em] text-slate-500">Modo de desembolso</div>
              <div className="mt-1 flex items-center gap-2 justify-end flex-wrap">
                <span className="font-mono font-bold text-slate-900">{overview.payout_mode}</span>
                <span data-testid="admin-finance-atomic" title="MongoDB standalone — reintentos idempotentes protegen la consistencia">
                  {overview.atomic_cascades ? (
                    <Badge className="rounded-none border bg-emerald-100 text-emerald-800 border-emerald-200">
                      <ShieldCheck className="h-3 w-3 mr-1" /> Cascadas atómicas
                    </Badge>
                  ) : (
                    <Badge className="rounded-none border bg-amber-100 text-amber-800 border-amber-200">
                      <ShieldOff className="h-3 w-3 mr-1" /> No atómico (idempotente)
                    </Badge>
                  )}
                </span>
              </div>
              <div className="text-slate-500 mt-1 max-w-xs">{PAYOUT_MODE_LABEL[overview.payout_mode] || overview.payout_mode}</div>
            </div>
          )}
        </div>

        <AmountMismatchAlert />

        {/* Tabs */}
        <div className="mt-6 border-b border-slate-200 flex gap-6 text-sm font-bold uppercase tracking-wider" data-testid="admin-finance-tabs">
          <TabBtn active={tab==='automarket'} onClick={()=>setTabAndUrl('automarket')} testid="tab-automarket">
            Ingresos AutoMarket
          </TabBtn>
          <TabBtn active={tab==='seller'} onClick={()=>setTabAndUrl('seller')} testid="tab-seller">
            Dinero del vendedor
          </TabBtn>
          <TabBtn active={tab==='refunds'} onClick={()=>setTabAndUrl('refunds')} testid="tab-refunds">
            Reembolsos
          </TabBtn>
          <TabBtn active={tab==='withdrawals'} onClick={()=>setTabAndUrl('withdrawals')} testid="tab-withdrawals">
            Retiros manuales
          </TabBtn>
        </div>

        <div className="mt-6">
          {tab === 'automarket' && <AutoMarketPanel overview={overview} onRefresh={refreshOverview} />}
          {tab === 'seller' && <SellerMoneyPanel overview={overview} onRefresh={refreshOverview} />}
          {tab === 'refunds' && <RefundsPanel onRefresh={refreshOverview} />}
          {tab === 'withdrawals' && (
            <div className="border border-slate-200 bg-white p-6" data-testid="withdrawals-legacy-cta">
              <div className="text-slate-700 text-sm">
                Los retiros manuales se administran en la vista clásica. Se mantendrán activos hasta que el gateway soporte desembolsos automáticos.
              </div>
              <Link to="/admin/retiros" data-testid="withdrawals-legacy-link">
                <Button className="mt-3 rounded-none bg-slate-900 hover:bg-slate-800">
                  Ir a retiros manuales <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`pb-3 -mb-px border-b-2 transition ${active ? 'border-orange-500 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
  >{children}</button>
);

/**
 * Toolbar with date range + Excel/CSV download buttons. Downloads the file
 * from the given endpoint applying the current filters + date range. Uses
 * fetch+blob so the browser triggers a real download with the server-provided
 * filename.
 */
function ExportBar({ endpoint, extraParams = {}, testid, className = '' }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [downloading, setDownloading] = useState(null);

  const download = async (fmt) => {
    setDownloading(fmt);
    const params = new URLSearchParams();
    params.set('format', fmt);
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    Object.entries(extraParams).forEach(([k, v]) => {
      if (v && v !== 'all') params.set(k, v);
    });
    try {
      const token = localStorage.getItem('session_token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}${endpoint}?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      // Extract filename from Content-Disposition
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `export.${fmt}`;
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      toast.success(`Descarga lista: ${filename}`);
    } catch (e) {
      toast.error(`No se pudo descargar (${e.message || 'error'})`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div data-testid={testid} className={`flex items-center gap-2 flex-wrap ${className}`}>
      <div className="inline-flex items-center gap-1 text-xs text-slate-500 font-bold uppercase tracking-wider">
        <Calendar className="h-3 w-3" /> Rango
      </div>
      <Input
        data-testid={`${testid}-from`}
        type="date"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        className="rounded-none h-9 w-40 font-mono"
      />
      <span className="text-slate-400">→</span>
      <Input
        data-testid={`${testid}-to`}
        type="date"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        className="rounded-none h-9 w-40 font-mono"
      />
      <Button
        data-testid={`${testid}-xlsx-btn`}
        variant="outline"
        onClick={() => download('xlsx')}
        disabled={downloading}
        className="rounded-none border-emerald-500 text-emerald-700 hover:bg-emerald-500 hover:text-slate-900"
      >
        <FileSpreadsheet className="h-4 w-4 mr-1" />
        {downloading === 'xlsx' ? 'Generando…' : 'Excel'}
      </Button>
      <Button
        data-testid={`${testid}-csv-btn`}
        variant="outline"
        onClick={() => download('csv')}
        disabled={downloading}
        className="rounded-none border-slate-300"
      >
        <FileText className="h-4 w-4 mr-1" />
        {downloading === 'csv' ? 'Generando…' : 'CSV'}
      </Button>
    </div>
  );
}

// ─── Panels ─────────────────────────────────────────────────────────────
function AutoMarketPanel({ overview }) {
  const [items, setItems] = useState([]);
  const [conceptFilter, setConceptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sellerFilter, setSellerFilter] = useState('');

  const refresh = useCallback(() => {
    const params = {};
    if (conceptFilter !== 'all') params.concept = conceptFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (sellerFilter.trim()) params.seller_id = sellerFilter.trim();
    return api.get('/admin/finance/revenue', { params }).then(({ data }) => setItems(data.items || []));
  }, [conceptFilter, statusFilter, sellerFilter]);
  useEffect(() => { refresh(); }, [refresh]);

  const totals = overview?.automarket_income?.by_concept || {};

  return (
    <div data-testid="panel-automarket">
      {/* 4 concept cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(CONCEPT_META).map(([k, meta]) => {
          const T = totals[k] || { settled: 0, expected: 0, reversed: 0, count: 0 };
          const Icon = meta.icon;
          return (
            <div key={k} data-testid={`revenue-card-${k}`} className={`border p-4 ${meta.tone}`}>
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <div className="text-[10px] font-bold uppercase tracking-[0.2em]">{meta.label}</div>
              </div>
              <div className="mt-2 font-display text-2xl font-black">{formatCOP(T.settled)}</div>
              <div className="text-xs mt-1 opacity-80">
                {T.expected > 0 && <>· <span className="font-bold">{formatCOP(T.expected)}</span> esperado</>}
                {T.reversed > 0 && <> · {formatCOP(T.reversed)} revertido</>}
                <div className="opacity-70">{T.count} movimientos</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Select value={conceptFilter} onValueChange={setConceptFilter}>
          <SelectTrigger data-testid="revenue-concept-filter" className="w-56 rounded-none"><SelectValue placeholder="Concepto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los conceptos</SelectItem>
            {Object.entries(CONCEPT_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="revenue-status-filter" className="w-48 rounded-none"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="expected">Esperado</SelectItem>
            <SelectItem value="settled">Confirmado</SelectItem>
            <SelectItem value="reversed">Revertido</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid="revenue-seller-filter"
          value={sellerFilter}
          onChange={(e) => setSellerFilter(e.target.value)}
          placeholder="ID vendedor (opcional)"
          className="rounded-none h-9 w-64 font-mono"
        />
      </div>

      <ExportBar
        testid="export-revenue"
        endpoint="/api/admin/finance/export/revenue"
        extraParams={{
          concept: conceptFilter,
          status: statusFilter,
          seller_id: sellerFilter.trim(),
        }}
        className="mt-3"
      />

      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Concepto</th>
              <th className="px-4 py-3">Referencia</th>
              <th className="px-4 py-3">Vendedor</th>
              <th className="px-4 py-3 text-right">Monto</th>
              <th className="px-4 py-3">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="revenue-ledger-table">
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Sin movimientos</td></tr>}
            {items.map((r) => {
              const meta = CONCEPT_META[r.concept];
              const st = REVENUE_STATUS[r.status] || { label: r.status, cls: 'bg-slate-100' };
              return (
                <tr key={r.revenue_id} className="row-hover align-top">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(r.occurred_at)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 font-bold">
                      {meta && <meta.icon className="h-3 w-3" />} {meta?.label || r.concept}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-600">{r.source_ref}</td>
                  <td className="px-4 py-3 text-xs">{r.seller_id || '—'}</td>
                  <td className="px-4 py-3 font-bold text-right">{formatCOP(r.amount)}</td>
                  <td className="px-4 py-3"><Badge className={`rounded-none border ${st.cls}`}>{st.label}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SellerMoneyPanel({ overview, onRefresh }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ready');
  const [sellerFilter, setSellerFilter] = useState('');
  const [active, setActive] = useState(null);
  const [transitionTo, setTransitionTo] = useState('');
  const [note, setNote] = useState('');
  const [gatewayTxn, setGatewayTxn] = useState('');
  const [indeterminateAck, setIndeterminateAck] = useState(false);

  const refresh = useCallback(() => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (sellerFilter.trim()) params.seller_id = sellerFilter.trim();
    return api.get('/admin/finance/payouts', { params }).then(({ data }) => setItems(data.items || []));
  }, [statusFilter, sellerFilter]);
  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    try {
      await api.put(`/admin/finance/payouts/${active.payout_id}`, {
        new_status: transitionTo,
        note: note || undefined,
        gateway_transaction_id: gatewayTxn || undefined,
      });
      toast.success(`Payout movido a ${PAYOUT_STATUS[transitionTo]?.label || transitionTo}`);
      setActive(null); setNote(''); setGatewayTxn('');
      refresh();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo procesar');
    }
  };

  const buckets = overview?.seller_money?.payouts?.by_status || {};
  const [sweeping, setSweeping] = useState(false);
  const [confirmForceOpen, setConfirmForceOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diag, setDiag] = useState(null);
  const openWompiDiag = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const { data } = await api.get('/admin/finance/wompi/diagnostics');
      setDiag(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo consultar Wompi');
    } finally {
      setDiagLoading(false);
    }
  };
  const runSweep = async (force = false) => {
    if (sweeping) return;
    setSweeping(true);
    try {
      const { data } = await api.post(`/admin/payouts/sweep${force ? '?force=true' : ''}`);
      // Backend flattens the sweep result at the top level (no `result` wrapper):
      //   { ok:true, evaluate:{scheduled, held, waited, noop}, retry:{...},
      //     dispatch:{sent, failed, skipped}, poll:{completed, still_sent, unknown},
      //     alerts:{...}, mode }
      const ev = data?.evaluate || {};
      const dp = data?.dispatch || {};
      const pl = data?.poll || {};
      toast.success(
        `Sweep OK${force ? ' (force)' : ''} · evaluate scheduled=${ev.scheduled ?? 0} · held=${ev.held ?? 0} · waited=${ev.waited ?? 0} · noop=${ev.noop ?? 0} — dispatch sent=${dp.sent ?? 0} · failed=${dp.failed ?? 0} — poll completed=${pl.completed ?? 0}`,
        { duration: 14000 },
      );
      refresh();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo ejecutar el sweep');
    } finally {
      setSweeping(false);
      setConfirmForceOpen(false);
    }
  };

  return (
    <div data-testid="panel-seller">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {Object.entries(PAYOUT_STATUS).map(([k, meta]) => {
          const T = buckets[k] || { amount: 0, count: 0 };
          const Icon = meta.icon;
          return (
            <button
              key={k}
              data-testid={`bucket-${k}`}
              onClick={() => setStatusFilter(k)}
              className={`text-left border p-3 transition hover:border-slate-900 ${statusFilter === k ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white'}`}
            >
              <div className="flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5 text-slate-600" />
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{meta.label}</div>
              </div>
              <div className="mt-1 font-display font-black text-slate-900">{formatCOP(T.amount)}</div>
              <div className="text-[10px] text-slate-500">{T.count} pagos</div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Input
          data-testid="payouts-seller-filter"
          value={sellerFilter}
          onChange={(e) => setSellerFilter(e.target.value)}
          placeholder="ID vendedor (opcional)"
          className="rounded-none h-9 w-64 font-mono"
        />
        <ExportBar
          testid="export-payouts"
          endpoint="/api/admin/finance/export/payouts"
          extraParams={{
            status: statusFilter === 'all' ? '' : statusFilter,
            seller_id: sellerFilter.trim(),
          }}
        />
        <Button
          data-testid="run-sweep-btn"
          onClick={() => runSweep(false)}
          disabled={sweeping}
          className="rounded-none h-9 bg-slate-900 hover:bg-slate-800 ml-auto"
          size="sm"
        >
          {sweeping ? 'Ejecutando…' : 'Ejecutar sweep ahora'}
        </Button>
        <Button
          data-testid="force-sweep-btn"
          onClick={() => setConfirmForceOpen(true)}
          disabled={sweeping}
          variant="outline"
          className="rounded-none h-9 border-amber-500 text-amber-700 hover:bg-amber-50"
          size="sm"
        >
          Forzar sweep (omitir seguridad)
        </Button>
        <Button
          data-testid="wompi-diag-btn"
          onClick={openWompiDiag}
          disabled={diagLoading}
          variant="outline"
          className="rounded-none h-9 border-slate-400 text-slate-700 hover:bg-slate-50"
          size="sm"
        >
          {diagLoading ? 'Consultando…' : 'Diagnóstico Wompi'}
        </Button>
      </div>

      <Dialog open={confirmForceOpen} onOpenChange={setConfirmForceOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Forzar sweep — omitir ventana de seguridad</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <div className="font-bold uppercase tracking-wider text-[11px] mb-1">Acción sensible</div>
              <p>
                Esta acción ejecuta el sweep con <code className="font-mono">force=true</code>, saltándose la ventana de seguridad post-entrega (7 días por defecto) y despachando inmediatamente los payouts en <span className="font-bold">ready</span> hacia Wompi <span className="font-bold">Producción</span>.
              </p>
              <p className="mt-2">
                Úsalo solo cuando estés seguro de que las órdenes ya están entregadas y validadas. No se puede deshacer una vez que Wompi confirme el desembolso.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="rounded-none flex-1"
                onClick={() => setConfirmForceOpen(false)}
                disabled={sweeping}
              >
                Cancelar
              </Button>
              <Button
                data-testid="force-sweep-confirm-btn"
                onClick={() => runSweep(true)}
                disabled={sweeping}
                className="rounded-none flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {sweeping ? 'Ejecutando…' : 'Confirmar bypass y ejecutar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!diag} onOpenChange={(o) => { if (!o) setDiag(null); }}>
        <DialogContent className="rounded-none max-w-3xl">
          <DialogHeader>
            <DialogTitle>Diagnóstico Wompi Pagos a Terceros</DialogTitle>
          </DialogHeader>
          {diag && (
            <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-slate-200 p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Modo</div>
                  <div className="font-mono">{diag.mode}</div>
                </div>
                <div className="border border-slate-200 p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Base URL</div>
                  <div className="font-mono text-[11px] break-all">{diag.base_url}</div>
                </div>
              </div>

              <div className={`border p-3 ${diag.is_valid_payout_source ? 'border-emerald-300 bg-emerald-50' : 'border-rose-300 bg-rose-50'}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold mb-1">
                  ACCOUNT_ID configurado
                </div>
                <div className="font-mono text-xs break-all">{diag.configured_account_id || '(vacío)'}</div>
                <div className={`mt-1 text-xs font-bold ${diag.is_valid_payout_source ? 'text-emerald-800' : 'text-rose-800'}`}>
                  {diag.is_valid_payout_source
                    ? `✓ Es una fuente válida de payouts (saldo ${((diag.configured_account_balance_cents ?? 0) / 100).toLocaleString('es-CO')} COP)`
                    : diag.configured_account_id_matches_any_returned
                      ? `✗ Existe pero NO tiene saldo — no puede ser fuente de payouts. Usa: ${diag.account_with_balance_id ?? 'la cuenta con saldo abajo'}`
                      : '✗ NO coincide con ninguna cuenta devuelta por Wompi'}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                  Cuentas devueltas por Wompi ({diag.accounts?.accounts?.length ?? 0})
                  {diag.accounts?.error && (
                    <span className="ml-2 text-rose-700">— error: {diag.accounts.error}</span>
                  )}
                </div>
                {(diag.accounts?.accounts || []).length === 0 && !diag.accounts?.error && (
                  <div className="text-slate-500 italic p-3 border border-dashed border-slate-300 text-xs">
                    Wompi no devolvió cuentas para este principal.
                  </div>
                )}
                <div className="space-y-2">
                  {(diag.accounts?.accounts || []).map((a) => {
                    const isConfigured = a.id === diag.configured_account_id;
                    return (
                      <div key={a.id} className={`border p-3 ${isConfigured ? 'border-slate-900 bg-slate-50' : 'border-slate-200'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-xs break-all">{a.id}</div>
                          {isConfigured && <Badge className="rounded-none">Configurado</Badge>}
                        </div>
                        <div className="mt-1 text-xs text-slate-600">
                          {a.name && <span>{a.name} · </span>}
                          {a.bank && <span>{a.bank} · </span>}
                          {a.status && <span>estado: {a.status} · </span>}
                          {a.raw?.balanceInCents != null && (
                            <span>saldo: {(a.raw.balanceInCents / 100).toLocaleString('es-CO')} COP</span>
                          )}
                          {a.raw?.isMain && <span> · principal</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border border-slate-200 p-3 bg-slate-50 text-xs">
                <span className="font-bold">Bancos:</span> {diag.banks?.count ?? 0} en catálogo
                {diag.banks?.error && <span className="text-rose-700"> — {diag.banks.error}</span>}
              </div>

              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 font-bold">Raw response</summary>
                <pre className="mt-2 p-3 bg-slate-900 text-slate-100 overflow-x-auto text-[10px]">
{JSON.stringify(diag.accounts?.raw, null, 2)}
                </pre>
              </details>

              <div className="border border-blue-300 bg-blue-50 p-3 text-xs text-blue-900">
                <div className="font-bold uppercase tracking-wider mb-1">Cómo usar</div>
                <p>
                  Identifica la cuenta que corresponde a tu <span className="font-bold">Wompi Cuenta</span> con saldo disponible (normalmente <code className="font-mono">WOMPI_ACCOUNT</code>). Si el ID marcado como Configurado no es esa cuenta, actualiza <code className="font-mono">WOMPI_TRANSFERS_ACCOUNT_ID</code> en Deployment Panel → Secrets y republica.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Payout</th>
              <th className="px-4 py-3">Pedido</th>
              <th className="px-4 py-3">Vendedor</th>
              <th className="px-4 py-3 text-right">Bruto</th>
              <th className="px-4 py-3 text-right">Comisión</th>
              <th className="px-4 py-3 text-right">Neto vendedor</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="payouts-ledger-table">
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">Sin payouts en este estado</td></tr>}
            {items.map((p) => {
              const st = PAYOUT_STATUS[p.status] || { label: p.status, cls: 'bg-slate-100' };
              // Iter 47.3 · rescate desde UI: `completed` sigue oculto por
              // defecto (nada que hacer con evidencia Wompi real), pero se
              // habilita el botón "Mover" cuando gid=null (fake-completed)
              // para que el operador pueda hacer `completed → ready` sin
              // recurrir a curl. `refunded` sigue terminal.
              const isFakeCompleted = p.status === 'completed' && !p.gateway_transaction_id;
              // Iter 47.6 · Un payout `held` con retry_exhausted=true se
              // considera "atascado por reintentos agotados" — el sweeper no
              // lo va a tocar solo. Botón dedicado para reciclarlo a `ready`
              // (el backend resetea retry_count/retry_exhausted).
              const isStuckHeld = p.status === 'held' && p.retry_exhausted;
              const canTransition = (p.status !== 'completed' || isFakeCompleted) && p.status !== 'refunded';
              return (
                <tr key={p.payout_id} className="row-hover align-top">
                  <td className="px-4 py-3 font-mono text-[11px]">{p.payout_id}</td>
                  <td className="px-4 py-3 font-mono text-[11px]">{p.order_id}</td>
                  <td className="px-4 py-3 text-xs">{p.seller_id}</td>
                  <td className="px-4 py-3 text-right">{formatCOP(p.gross)}</td>
                  <td className="px-4 py-3 text-right text-rose-700">−{formatCOP(p.commission)}</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-900">{formatCOP(p.net)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-none border ${st.cls}`}>{st.label}</Badge>
                    {isFakeCompleted && (
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                        Sin evidencia Wompi
                      </div>
                    )}
                    {isStuckHeld && (
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-rose-700">
                        Reintentos agotados
                      </div>
                    )}
                    {p.last_retry_error && (p.status === 'held' || p.status === 'failed') && (
                      <div className="mt-1 text-[10px] text-slate-500 font-mono break-all max-w-xs" title={p.last_retry_error}>
                        {String(p.last_retry_error).slice(0, 70)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.status === 'held' && Number(p.net || 0) <= 0 && !p.gateway_transaction_id && (
                      <Button data-testid={`payout-settle-zero-${p.payout_id}`} size="sm" variant="outline" className="mr-2 rounded-none"
                        onClick={async () => {
                          const reason = window.prompt('Motivo para liquidar sin transferencia (mín. 10 caracteres). No se enviará dinero.');
                          if (!reason) return;
                          try { await api.post(`/admin/finance/payouts/${p.payout_id}/settle-zero`, { reason }); toast.success('Liquidado sin transferencia'); refresh(); }
                          catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'No se pudo liquidar'); }
                        }}>
                        Liquidar sin transferencia
                      </Button>
                    )}
                    {canTransition && (
                      <Button
                        data-testid={`payout-transition-${p.payout_id}`}
                        size="sm"
                        variant={(isFakeCompleted || isStuckHeld) ? 'default' : 'outline'}
                        className={`rounded-none ${(isFakeCompleted || isStuckHeld) ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
                        onClick={() => { setActive(p); setTransitionTo(nextPayoutStatus(p.status, p) || ''); }}
                      >
                        {isFakeCompleted ? 'Rescatar → ready' : isStuckHeld ? 'Reintentar → ready' : 'Mover'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setNote(''); setGatewayTxn(''); setIndeterminateAck(false); } }}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Cambiar estado del payout</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">
                Payout <span className="font-mono">{active.payout_id}</span> · Neto {formatCOP(active.net)} · Actual: <span className="font-bold">{PAYOUT_STATUS[active.status]?.label}</span>
              </div>
              {active.indeterminate_reason && transitionTo === 'ready' && (
                <div className="border border-rose-400 bg-rose-50 p-3 text-xs text-rose-900">
                  <div className="font-bold uppercase tracking-wider mb-1">⚠️ Resultado indeterminado</div>
                  <p>
                    Este payout fue enviado a Wompi pero el resultado nunca pudo confirmarse ({active.indeterminate_reason}). Puede que ya se haya desembolsado del lado Wompi. Si lo reintentas sin verificar, corres el riesgo de <span className="font-bold">doble pago</span>.
                  </p>
                  <label className="flex items-start gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="indeterminate-ack-checkbox"
                      checked={indeterminateAck}
                      onChange={(e) => setIndeterminateAck(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>He verificado en el dashboard de Wompi que este payout NO fue pagado. Autorizo el reintento.</span>
                  </label>
                </div>
              )}
              <Select value={transitionTo} onValueChange={setTransitionTo}>
                <SelectTrigger data-testid="payout-transition-select" className="rounded-none"><SelectValue placeholder="Nuevo estado" /></SelectTrigger>
                <SelectContent>
                  {allowedTransitions(active.status, active).map((s) => (
                    <SelectItem key={s} value={s}>{PAYOUT_STATUS[s]?.label || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(transitionTo === 'sent' || transitionTo === 'completed') && (
                <Input
                  data-testid="payout-gateway-txn"
                  value={gatewayTxn}
                  onChange={(e) => setGatewayTxn(e.target.value)}
                  placeholder="ID de transacción del gateway (opcional)"
                  className="rounded-none"
                />
              )}
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Nota interna (opcional)"
                rows={3}
                className="rounded-none"
              />
              <Button
                data-testid="payout-confirm-btn"
                onClick={submit}
                disabled={!transitionTo || (active.indeterminate_reason && transitionTo === 'ready' && !indeterminateAck)}
                className="w-full rounded-none bg-slate-900 hover:bg-slate-800"
              >
                Confirmar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RefundsPanel({ onRefresh }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ order_id: '', reason: '', amount: '' });
  const [active, setActive] = useState(null);
  const [transitionTo, setTransitionTo] = useState('');
  const [note, setNote] = useState('');

  const refresh = useCallback(() => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    return api.get('/admin/finance/refunds', { params }).then(({ data }) => setItems(data.items || []));
  }, [statusFilter]);
  useEffect(() => { refresh(); }, [refresh]);

  const createRefund = async () => {
    if (!newForm.order_id.trim() || !newForm.reason.trim()) {
      toast.error('Pedido y razón son obligatorios');
      return;
    }
    try {
      await api.post('/admin/finance/refunds', {
        order_id: newForm.order_id.trim(),
        reason: newForm.reason.trim(),
        amount: newForm.amount ? parseFloat(newForm.amount) : undefined,
      });
      toast.success('Reembolso creado');
      setCreating(false); setNewForm({ order_id: '', reason: '', amount: '' });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear');
    }
  };

  const transition = async () => {
    try {
      await api.put(`/admin/finance/refunds/${active.refund_id}`, {
        new_status: transitionTo,
        note: note || undefined,
      });
      toast.success(`Reembolso: ${REFUND_STATUS[transitionTo]?.label}`);
      setActive(null); setNote('');
      refresh();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo procesar');
    }
  };

  return (
    <div data-testid="panel-refunds">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="refunds-status-filter" className="w-48 rounded-none"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="requested">Solicitados</SelectItem>
            <SelectItem value="approved">Aprobados</SelectItem>
            <SelectItem value="processed">Procesados</SelectItem>
            <SelectItem value="rejected">Rechazados</SelectItem>
          </SelectContent>
        </Select>
        <Button
          data-testid="refund-create-btn"
          onClick={() => setCreating(true)}
          className="rounded-none bg-slate-900 hover:bg-slate-800"
        >
          <RefreshCcw className="h-4 w-4 mr-2" /> Nuevo reembolso
        </Button>
      </div>

      <ExportBar
        testid="export-refunds"
        endpoint="/api/admin/finance/export/refunds"
        extraParams={{ status: statusFilter === 'all' ? '' : statusFilter }}
        className="mt-3"
      />

      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">ID</th>
              <th className="px-4 py-3">Pedido</th>
              <th className="px-4 py-3">Razón</th>
              <th className="px-4 py-3 text-right">Monto</th>
              <th className="px-4 py-3 text-right">Comisión revertida</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="refunds-table">
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Sin reembolsos</td></tr>}
            {items.map((r) => {
              const st = REFUND_STATUS[r.status] || { label: r.status, cls: 'bg-slate-100' };
              return (
                <tr key={r.refund_id} className="row-hover align-top">
                  <td className="px-4 py-3 font-mono text-[11px]">{r.refund_id}</td>
                  <td className="px-4 py-3 font-mono text-[11px]">{r.order_id}</td>
                  <td className="px-4 py-3 text-xs">{r.reason}</td>
                  <td className="px-4 py-3 text-right">{formatCOP(r.amount)}</td>
                  <td className="px-4 py-3 text-right text-rose-700">−{formatCOP(r.commission_reversed)}</td>
                  <td className="px-4 py-3"><Badge className={`rounded-none border ${st.cls}`}>{st.label}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    {r.status !== 'processed' && r.status !== 'rejected' && (
                      <Button
                        data-testid={`refund-transition-${r.refund_id}`}
                        size="sm" variant="outline" className="rounded-none"
                        onClick={() => { setActive(r); setTransitionTo(nextRefundStatus(r.status) || ''); }}
                      >
                        Procesar
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Nuevo reembolso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="refund-order-input"
              value={newForm.order_id}
              onChange={(e) => setNewForm({ ...newForm, order_id: e.target.value })}
              placeholder="ord_xxxxxxxxxx"
              className="rounded-none font-mono"
            />
            <Textarea
              data-testid="refund-reason-input"
              value={newForm.reason}
              onChange={(e) => setNewForm({ ...newForm, reason: e.target.value })}
              placeholder="Razón del reembolso"
              rows={3}
              className="rounded-none"
            />
            <Input
              data-testid="refund-amount-input"
              value={newForm.amount}
              onChange={(e) => setNewForm({ ...newForm, amount: e.target.value })}
              placeholder="Monto (opcional, por defecto = total del pedido)"
              className="rounded-none"
            />
            <Button
              data-testid="refund-create-submit"
              onClick={createRefund}
              className="w-full rounded-none bg-slate-900 hover:bg-slate-800"
            >
              Registrar reembolso
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transition dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setNote(''); } }}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Actualizar estado del reembolso</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">
                <span className="font-mono">{active.refund_id}</span> · Actual: <span className="font-bold">{REFUND_STATUS[active.status]?.label}</span>
              </div>
              <Select value={transitionTo} onValueChange={setTransitionTo}>
                <SelectTrigger data-testid="refund-transition-select" className="rounded-none"><SelectValue placeholder="Nuevo estado" /></SelectTrigger>
                <SelectContent>
                  {allowedRefundTransitions(active.status).map((s) => (
                    <SelectItem key={s} value={s}>{REFUND_STATUS[s]?.label || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Nota (opcional)"
                rows={3}
                className="rounded-none"
              />
              <Button
                data-testid="refund-confirm-btn"
                onClick={transition}
                disabled={!transitionTo}
                className="w-full rounded-none bg-slate-900 hover:bg-slate-800"
              >
                Confirmar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────
function nextPayoutStatus(current, payout) {
  const t = allowedTransitions(current, payout);
  return t[0] || '';
}
function allowedTransitions(current, payout) {
  const map = {
    pending_delivery: ['ready', 'held', 'refunded'],
    ready:            ['scheduled', 'held', 'refunded'],  // `sent` removido: solo sweeper con UUID
    scheduled:        ['held', 'refunded'],                // `sent` removido: solo sweeper con UUID
    // `ready` habilitado sólo para rewind de payouts que jamás llegaron a Wompi
    // (backend guarda contra rewinds si gateway_transaction_id no es null).
    sent:             ['completed', 'held', 'ready'],
    // Recuperación: `completed → ready` SOLO cuando gid=null (fake completed).
    completed:        [],
    held:             ['ready', 'refunded'],
    refunded:         [],
  };
  let base = map[current] || [];
  // Data-integrity: `completed` solo aparece si hay gateway_transaction_id real.
  // El backend rechaza igualmente, esto es defensa en profundidad.
  if (payout && !payout.gateway_transaction_id) {
    base = base.filter((s) => s !== 'completed');
  }
  // Recuperación fake-completed: agrega `ready` a `completed` solo si gid=null.
  if (current === 'completed' && payout && !payout.gateway_transaction_id) {
    base = ['ready'];
  }
  return base;
}
function nextRefundStatus(current) {
  const t = allowedRefundTransitions(current);
  return t[0] || '';
}
function allowedRefundTransitions(current) {
  const map = {
    requested: ['approved', 'rejected'],
    approved:  ['processed', 'rejected'],
    processed: [],
    rejected:  [],
  };
  return map[current] || [];
}
